import React, { useEffect, useRef } from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { Plus, ArrowLeft } from "lucide-react";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import useCreateTrack from "../hooks/use-create-track.hook";
import CreateTrackRow from "./create-track-row";

interface CreateTrackContentProps {
   onClose: () => void;
   onBack?: () => void;
}

 
const CreateTrackContent: React.FC<CreateTrackContentProps> = ({
  onClose,
  onBack,
}) => {
  const {
    createdTracks,
    handleAddTrack,
    handleDeleteTrack,
    handleTrackChange,
    isSubmitting,
    handleSubmit,
    isValid,
    canAddNewTrack,
    trackErrors,
    registerRowFormApi,
    setTrackFormLoading,
  } = useCreateTrack({ onClose });

  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (createdTracks.length > 0) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [createdTracks.length]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <Box
        sx={{
          padding: "24px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: nawyTheme.nawy.spacing.md,
          maxHeight: "60vh",
          overflowY: "auto",
        }}
      >
        <Typography sx={{ fontSize: "14px", color: "#636363" }}>
          Add one or more developer tracks.
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {createdTracks.map((track, index) => (
            <CreateTrackRow
              key={track.id}
              track={track}
              index={index}
              onDelete={handleDeleteTrack}
              onChange={handleTrackChange}
              canDelete={createdTracks.length > 1}
              errors={trackErrors[track.id]}
              onRegisterFormApi={registerRowFormApi}
              onFormLoadingChange={setTrackFormLoading}
            />
          ))}
          <div ref={bottomRef} />
        </Box>

        <Button
          startIcon={<Plus size={18} />}
          onClick={handleAddTrack}
          disabled={!canAddNewTrack}
          sx={{
            width: "fit-content",
            textTransform: "none",
            color: nawyTheme.nawy.colors.semantic.layer.accent,
            ...nawyTheme.nawy.typography.english.variants.body.md.semibold,
            "&:hover": {
              backgroundColor: "transparent",
              textDecoration: "underline",
            },
            padding: 0,
            marginTop: 1,
          }}
        >
          Add another track
        </Button>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: nawyTheme.nawy.spacing.sm,
          padding: `${nawyTheme.nawy.spacing.sm} 24px`,
          borderTop: `1px solid ${nawyTheme.nawy.colors.semantic.border.primary}`,
        }}
      >
        <Button
          onClick={onBack ?? onClose}
          startIcon={onBack ? <ArrowLeft size={16} /> : undefined}
          sx={{
            textTransform: "none",
            color: nawyTheme.nawy.colors.semantic.text.secondary,
            ...nawyTheme.nawy.typography.english.variants.body.md.regular,
          }}
        >
          {onBack ? "Back" : "Cancel"}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!isValid || isSubmitting}
          sx={{
            textTransform: "none",
            borderRadius: nawyTheme.nawy.radius.lg,
            padding: `${nawyTheme.nawy.spacing.sm} ${nawyTheme.nawy.spacing.lg}`,
            ...nawyTheme.nawy.typography.english.variants.body.md.semibold,
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : (
            "Create"
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default CreateTrackContent;
